import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../logic/store/Store';
import { validateToken, clearState, silentValidateToken } from '../../logic/store/User/AuthSlice';

const maxAllowedNetworkErrorsOnValidate = 2;
const tokenValidationIntervalSeconds = 5 * 60; // 5 minutes

/**
 * AuthMonitor component handles periodic token validation.
 * 
 * Runs a timer that validates the authentication token every [N] minutes
 * when the user is authenticated. If validation fails, logs the user out.
 * 
 * This component renders nothing and should be mounted once at the app 
 * level (but inside the router).
 */
export const AuthMonitor = () => 
{
	const dispatch = useAppDispatch();
	const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
	const userAuthToken = useAppSelector(state => state.auth.userAuthToken);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);
	const errorCount = useRef<number>(0);

	useEffect(() => 
	{
		// Only run the timer when authenticated
		if (isAuthenticated) 
		{
			// Test once immediately -- for example on page load.  This will show a blocking load spinner.
			ValidateCurrentAuthentication(dispatch, userAuthToken, false, errorCount);

			// Set up interval to validate token every [N] minutes silently (won't show a loading spinner)
			intervalRef.current = setInterval(async () => 
			{
				await ValidateCurrentAuthentication(dispatch, userAuthToken, true, errorCount);
			}, tokenValidationIntervalSeconds * 1000); 
		}

		// Cleanup function
		return () => 
		{
			if (intervalRef.current) 
			{
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [isAuthenticated, userAuthToken, dispatch]);

	// This component renders nothing
	return null;
};

/**
 * Validates the current authentication token and handles various failure scenarios
 * 
 * @param dispatch - Redux dispatch function
 * @param userAuthToken - Current user authentication token
 * @param silent - If true, uses silent validation that won't show loading spinners
 * @param errorCount - Reference to track consecutive network errors
 * 
 * @remarks
 * - On validation failure, logs out the user
 * - Network errors are tolerated up to maxAllowedNetworkErrorsOnValidate before logout
 * - Silent validation is used for periodic background checks
 */
async function ValidateCurrentAuthentication(
	dispatch: ReturnType<typeof useAppDispatch>, 
	userAuthToken: string, 
	silent: boolean,
	errorCount: React.MutableRefObject<number>) 
{
	const result = await dispatch(silent ? silentValidateToken(userAuthToken) : validateToken(userAuthToken));

	// If validation failed explicitly, log out the user
	if (validateToken.rejected.match(result)) 
	{
		dispatch(clearState('Session expired. Please log in again.'));
	}

	else if(silentValidateToken.rejected.match(result))
	{
		if((result.payload as any).type === 'network_error')
		{
			// On a network error, log out the user if we've hit our tolerance -- we may allow one failed attempt 
			// to mitigate transient network issues, for example
			console.error('Token validation error:', result.error);
			if(errorCount.current >= maxAllowedNetworkErrorsOnValidate)
			{
				dispatch(clearState('There was an error with your session. Please log in again.'));
			}
			else
			{
				errorCount.current += 1;
			}		
		}
		else
		{
			dispatch(clearState('Session expired. Please log in again.'));
		}
	}
	// The validation endpoint indicates it was not successful
	else if (result.payload === false)
	{
		dispatch(clearState('Session expired. Please log in again.'));	
	}
	else
	{
		errorCount.current = 0; // Reset error count on success
	}
}

